import type { RefObject } from 'react'
import { MicInput, ProceduralVoice } from './voice'

/** Where the DOM leaves room for the 3D, in CSS px relative to the section's top-left. */
export interface HeroLayout {
  width: number
  height: number
  /** The room the DOM reserves for the orb (desktop: the band between headline and lead). */
  stageTop: number
  stageBottom: number
  /** Stacked (phone / tall tablet) layout: orb above, text below. */
  stacked: boolean
  /** Bumped on every measurement, so the scene only re-solves its layout when needed. */
  version: number
}

/**
 * Mutable state shared by the DOM overlay and the R3F scene. The DOM writes it (GSAP
 * tweens, ScrollTrigger, Lenis, the mic); the scene reads it inside `useFrame`. Nothing in
 * here triggers a React render.
 */
export interface HeroState {
  /** Intro progress, tweened by GSAP: the orb resolving, each wordmark letter, the floor. */
  intro: { orb: number; floor: number; letters: { value: number }[] }
  /** Scroll-out progress 0..1 (written by a ScrollTrigger). */
  scroll: RefObject<number>
  /** Lenis velocity in px per frame-ish units (signed). */
  scrollVelocity: number
  layout: HeroLayout
  /** The section's (and canvas's) top-left in client px, kept current on scroll/resize. */
  rect: { top: number; left: number }
  voice: ProceduralVoice
  mic: MicInput
  /** The envelope the orb is speaking with right now (procedural or mic), for the DOM chip. */
  speaking: number
  /** Set by the scene: draws one frame on demand (reduced motion). */
  invalidate: (() => void) | null
}

/** Letters in the wordmark, for the per-letter rise. */
export const WORDMARK_LETTERS = 7

export function createHeroState(scroll: RefObject<number>): HeroState {
  return {
    intro: {
      orb: 0,
      floor: 0,
      letters: Array.from({ length: WORDMARK_LETTERS }, () => ({ value: 0 })),
    },
    scroll,
    scrollVelocity: 0,
    layout: { width: 0, height: 0, stageTop: 0, stageBottom: 0, stacked: false, version: 0 },
    rect: { top: 0, left: 0 },
    voice: new ProceduralVoice(),
    mic: new MicInput(),
    speaking: 0,
    invalidate: null,
  }
}
